var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { parseMonth } from "@ui/utils/month";
import React, { useState, useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { getAppToken, exchangeAppToken } from "../../../auth/appToken";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Box, Tabs, Tab, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, TablePagination, Avatar, useTheme, Button, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, TextField, useMediaQuery, IconButton, Skeleton, Tooltip, } from "@mui/material";
import { PageHeader } from "@ui/components/header/PageHeader";
import { WidgetLoader } from "@ui/components/loader/WidgetLoader";
import { NoModuleAccess } from "@ui/components/no-module-access/NoModuleAccess";
import { FilteredInput } from "@ui/components/inputs";
import { SearchNormal, Add, CloseCircle } from "iconsax-react";
import { Users } from "@ui/pages/users";
import RolePermissions from "@ui/pages/role-permission";
import { useGetAllCompaniesQuery } from "../../../features/projects/projectsApi";
import { useGetAllCompanyMonthConfigsQuery, useCreateCompanyMonthConfigMutation, useUpdateCompanyMonthConfigMutation, useBulkProcessCompanyMonthConfigsMutation, useLazyGetActiveBulkMarginJobsQuery, } from "../../../features/companyMonthConfigs/companyMonthConfigsApi";
import { API_BASE_URL } from "../../../config/app-config";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { baseApi } from "../../../features/api/baseApi";
import { usePermissions } from "../../../context/PermissionContext";
import { CompanyConfigCard } from "@ui/components/card/CompanyConfigCard";
import { CardPagination } from "@ui/components/pagination/CardPagination";
function TabPanel(props) {
    const { children, value, index } = props, other = __rest(props, ["children", "value", "index"]);
    return (React.createElement("div", Object.assign({ role: "tabpanel", hidden: value !== index, id: `settings-tabpanel-${index}`, "aria-labelledby": `settings-tab-${index}` }, other), value === index && React.createElement(Box, null, children)));
}
// Helper function to convert "2025-01-01" to "Jan, 2025"
const formatDateToMonthYear = (dateString) => {
    const date = parseMonth(dateString);
    if (!date)
        return dateString;
    const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${month}, ${year}`;
};
export const Settings = () => {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));
    const [tabValue, setTabValue] = useState(0); // Default to first tab
    // Inject a stylesheet that hides stuck DataGrid overlays. This is a targeted
    // CSS fix — no polling, no rAF loops, no MutationObserver needed.
    useEffect(() => {
        const styleId = "settings-datagrid-overlay-fix";
        if (document.getElementById(styleId))
            return;
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
      .MuiDataGrid-overlayWrapper,
      .MuiDataGrid-overlay,
      .MuiDataGrid-loadingOverlay,
      [class*="MuiDataGrid-overlay"] {
        display: none !important;
        pointer-events: none !important;
      }
    `;
        document.head.appendChild(style);
        return () => {
            var _a;
            (_a = document.getElementById(styleId)) === null || _a === void 0 ? void 0 : _a.remove();
        };
    }, []);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    // Bulk close confirmation dialog state
    const [bulkCloseDialogOpen, setBulkCloseDialogOpen] = useState(false);
    const [bulkCloseCompany, setBulkCloseCompany] = useState(null);
    const [bulkCloseLoading, setBulkCloseLoading] = useState(false);
    // Bulk process job tracking: companyId -> job status (in-memory only, restored from server on connect)
    const [activeJobs, setActiveJobs] = useState({});
    // Helper function to get current month in "MMM, YYYY" format
    const getCurrentMonth = () => {
        const now = new Date();
        const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ];
        return `${monthNames[now.getMonth()]}, ${now.getFullYear()}`;
    };
    const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
    const [openPickerId, setOpenPickerId] = useState(null);
    // Fetch all companies (for dropdown in add dialog)
    const { data: companiesData, isLoading: companiesLoading } = useGetAllCompaniesQuery();
    // Fetch all existing company configs
    const { data: allConfigsData, isLoading: configsLoading, isError: configsFailed, refetch: refetchConfigs, } = useGetAllCompanyMonthConfigsQuery();
    // Mutations for creating/updating configs
    const [createConfig] = useCreateCompanyMonthConfigMutation();
    const [updateConfig] = useUpdateCompanyMonthConfigMutation();
    const [bulkProcess] = useBulkProcessCompanyMonthConfigsMutation();
    const [fetchActiveJobs] = useLazyGetActiveBulkMarginJobsQuery();
    const { canCreate, canUpdate, canRead, isLoading: permissionsLoading, } = usePermissions();
    // SignalR connection ref
    const connectionRef = useRef(null);
    // Track which company groups we've joined so we can leave on unmount
    const joinedGroupsRef = useRef(new Set());
    // Map companyId -> companyName for notification messages
    const companyNameMapRef = useRef({});
    // Local state for companies with configs
    const [companies, setCompanies] = useState([]);
    // Load companies and their configs
    useEffect(() => {
        if (companiesData && allConfigsData) {
            // One row per company. The scheduler updates a company's single config in
            // place, so a second row for the same company is a stray duplicate; the
            // original (earliest created) row is the one carrying the user's values.
            // Row state below is keyed by companyId, so duplicates would also share
            // picker/checkbox state.
            const createdAt = (c) => c.createdDate ? new Date(c.createdDate).getTime() : Number.MAX_VALUE;
            const configByCompany = new Map();
            for (const config of allConfigsData) {
                const key = config.companyId.toLowerCase();
                const current = configByCompany.get(key);
                if (!current || createdAt(config) < createdAt(current)) {
                    configByCompany.set(key, config);
                }
            }
            const companyConfigs = [...configByCompany.values()]
                .map((config) => {
                var _a, _b;
                const company = companiesData.find((c) => c.entiteitId === config.companyId);
                if (!company)
                    return null;
                return {
                    id: config.id,
                    companyId: config.companyId,
                    name: company.entiteitNaam,
                    pocAanpassen: config.pocReadOnly,
                    lastUpdated: config.updatedDate || config.modifiedDate
                        ? new Date(config.updatedDate || config.modifiedDate || "").toLocaleDateString("en-US")
                        : config.createdDate
                            ? new Date(config.createdDate).toLocaleDateString("en-US")
                            : new Date().toLocaleDateString("en-US"),
                    forecastDataMonth: formatDateToMonthYear(config.configMonth),
                    thresholdAmount: (_a = config.thresholdAmount) !== null && _a !== void 0 ? _a : "",
                    marginPercentage: (_b = config.marginPercentage) !== null && _b !== void 0 ? _b : "",
                };
            })
                .filter((c) => c !== null);
            setCompanies(companyConfigs);
        }
    }, [companiesData, allConfigsData]);
    // ─── Fetch active jobs from server on mount (page load / refresh) ───
    useEffect(() => {
        fetchActiveJobs()
            .unwrap()
            .then((jobs) => {
            if (!jobs || jobs.length === 0)
                return;
            const restored = {};
            for (const job of jobs) {
                restored[job.companyId] = job;
            }
            setActiveJobs(restored);
        })
            .catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // ─── SignalR: connect to bulk-margin hub and listen for progress events ───
    // We key activeJobs by companyId. The backend sends companyId in
    // the group messages with `status`, `projectsProcessedSoFar`, etc.
    useEffect(() => {
        const baseUrl = API_BASE_URL.replace(/\/+$/, "");
        const hubUrl = `${baseUrl}/hubs/bulk-margin`;
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl, {
            accessTokenFactory: () => __awaiter(void 0, void 0, void 0, function* () {
                let token = getAppToken();
                if (!token) {
                    const exchanged = yield exchangeAppToken();
                    token = exchanged.status === "ok" ? exchanged.token : "";
                }
                return token !== null && token !== void 0 ? token : "";
            }),
        })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Warning)
            .build();
        // --- Progress event (Processing / Queued updates) ---
        connection.on("BulkMarginProgress", (progress) => {
            const companyId = progress.companyId;
            if (!companyId)
                return;
            setActiveJobs((prev) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
                return (Object.assign(Object.assign({}, prev), { [companyId]: {
                        jobId: (_c = (_a = progress.jobId) !== null && _a !== void 0 ? _a : (_b = prev[companyId]) === null || _b === void 0 ? void 0 : _b.jobId) !== null && _c !== void 0 ? _c : "",
                        companyId,
                        status: (_d = progress.status) !== null && _d !== void 0 ? _d : "Processing",
                        totalProjects: (_e = progress.totalProjects) !== null && _e !== void 0 ? _e : 0,
                        processedSoFar: (_f = progress.processedSoFar) !== null && _f !== void 0 ? _f : 0,
                        updated: (_g = progress.updated) !== null && _g !== void 0 ? _g : 0,
                        skipped: (_h = progress.skipped) !== null && _h !== void 0 ? _h : 0,
                        progressPercent: (_j = progress.progressPercent) !== null && _j !== void 0 ? _j : 0,
                        errorMessage: (_k = progress.errorMessage) !== null && _k !== void 0 ? _k : null,
                    } }));
            });
        });
        // --- Completed event ---
        connection.on("BulkMarginCompleted", (progress) => {
            var _a, _b, _c;
            const companyId = progress.companyId;
            if (!companyId)
                return;
            const companyName = (_a = companyNameMapRef.current[companyId]) !== null && _a !== void 0 ? _a : companyId;
            setActiveJobs((prev) => {
                const updated = Object.assign({}, prev);
                delete updated[companyId];
                return updated;
            });
            enqueueSnackbar(`Bulk verwerking voltooid voor ${companyName}: ${(_b = progress.updated) !== null && _b !== void 0 ? _b : 0} bijgewerkt, ${(_c = progress.skipped) !== null && _c !== void 0 ? _c : 0} overgeslagen`, { variant: "success" });
            setCompanies((prev) => prev.map((c) => c.companyId === companyId
                ? Object.assign(Object.assign({}, c), { lastUpdated: new Date().toLocaleDateString("en-US") }) : c));
            refetchConfigs();
            // The enqueue call already invalidated Projects, but the job writes after that; refetch now.
            dispatch(baseApi.util.invalidateTags(["Projects"]));
            // Leave group — no longer needed
            if (connection.state === signalR.HubConnectionState.Connected) {
                connection.invoke("LeaveCompanyGroup", companyId).catch(() => { });
            }
            joinedGroupsRef.current.delete(companyId);
        });
        // --- Failed event ---
        connection.on("BulkMarginFailed", (progress) => {
            var _a;
            const companyId = progress.companyId;
            if (!companyId)
                return;
            const companyName = (_a = companyNameMapRef.current[companyId]) !== null && _a !== void 0 ? _a : companyId;
            setActiveJobs((prev) => {
                const updated = Object.assign({}, prev);
                delete updated[companyId];
                return updated;
            });
            enqueueSnackbar(progress.errorMessage || `Bulk verwerking mislukt voor ${companyName}`, { variant: "error" });
            if (connection.state === signalR.HubConnectionState.Connected) {
                connection.invoke("LeaveCompanyGroup", companyId).catch(() => { });
            }
            joinedGroupsRef.current.delete(companyId);
        });
        // On connect/reconnect: fetch active jobs from server and join their SignalR groups.
        const restoreActiveJobs = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const jobs = yield fetchActiveJobs().unwrap();
                if (!jobs || jobs.length === 0) {
                    // No active jobs — clear any stale in-memory state
                    setActiveJobs({});
                    return;
                }
                const restored = {};
                for (const job of jobs) {
                    restored[job.companyId] = job;
                    // Join SignalR group so we receive live updates
                    if (!joinedGroupsRef.current.has(job.companyId)) {
                        yield connection
                            .invoke("JoinCompanyGroup", job.companyId)
                            .catch(() => { });
                        joinedGroupsRef.current.add(job.companyId);
                    }
                }
                setActiveJobs(restored);
            }
            catch (_a) {
                // silently ignore
            }
        });
        // Also restore on automatic reconnect
        connection.onreconnected(() => {
            restoreActiveJobs();
        });
        // Start connection
        connection
            .start()
            .then(() => {
            restoreActiveJobs();
        })
            .catch(() => { });
        connectionRef.current = connection;
        // Cleanup on unmount
        return () => {
            // Leave all joined groups gracefully
            if (connection.state === signalR.HubConnectionState.Connected) {
                const leavePromises = Array.from(joinedGroupsRef.current).map((cid) => connection.invoke("LeaveCompanyGroup", cid).catch(() => { }));
                Promise.all(leavePromises).finally(() => connection.stop());
            }
            else {
                connection.stop();
            }
            connectionRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };
    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);
    const handlePocToggle = (companyId) => __awaiter(void 0, void 0, void 0, function* () {
        const company = companies.find((c) => c.companyId === companyId);
        if (!company)
            return;
        const newValue = !company.pocAanpassen;
        try {
            if (company.id) {
                // Need update permission to modify existing config
                if (!canUpdate("CompanyConfigurations")) {
                    enqueueSnackbar("U heeft geen toestemming om deze configuratie bij te werken", {
                        variant: "error",
                    });
                    return;
                }
                yield updateConfig({
                    id: company.id,
                    companyId: company.companyId,
                    configMonth: company.forecastDataMonth,
                    pocReadOnly: newValue,
                }).unwrap();
            }
            else {
                // Need create permission to create new config
                if (!canCreate("CompanyConfigurations")) {
                    enqueueSnackbar("U heeft geen toestemming om configuraties aan te maken", {
                        variant: "error",
                    });
                    return;
                }
                const result = yield createConfig({
                    companyId: company.companyId,
                    configMonth: company.forecastDataMonth,
                    pocReadOnly: newValue,
                }).unwrap();
                // Update local state with the ID from the response
                setCompanies((prev) => prev.map((c) => c.companyId === companyId ? Object.assign(Object.assign({}, c), { id: result.id }) : c));
            }
            // Update local state
            setCompanies((prev) => prev.map((c) => c.companyId === companyId
                ? Object.assign(Object.assign({}, c), { pocAanpassen: newValue, lastUpdated: new Date().toLocaleDateString("en-US") }) : c));
            enqueueSnackbar("Configuratie succesvol bijgewerkt", {
                variant: "success",
            });
        }
        catch (_a) {
            enqueueSnackbar("Fout bij het bijwerken van configuratie", {
                variant: "error",
            });
        }
    });
    const handleForecastMonthChange = (companyId, value) => __awaiter(void 0, void 0, void 0, function* () {
        const company = companies.find((c) => c.companyId === companyId);
        if (!company)
            return;
        try {
            if (company.id) {
                if (!canUpdate("CompanyConfigurations")) {
                    enqueueSnackbar("U heeft geen toestemming om deze configuratie bij te werken", {
                        variant: "error",
                    });
                    return;
                }
                yield updateConfig({
                    id: company.id,
                    companyId: company.companyId,
                    configMonth: value,
                    pocReadOnly: company.pocAanpassen,
                }).unwrap();
            }
            else {
                if (!canCreate("CompanyConfigurations")) {
                    enqueueSnackbar("U heeft geen toestemming om configuraties aan te maken", {
                        variant: "error",
                    });
                    return;
                }
                const result = yield createConfig({
                    companyId: company.companyId,
                    configMonth: value,
                    pocReadOnly: company.pocAanpassen,
                }).unwrap();
                // Update local state with the ID from the response
                setCompanies((prev) => prev.map((c) => c.companyId === companyId ? Object.assign(Object.assign({}, c), { id: result.id }) : c));
            }
            // Update local state
            setCompanies((prev) => prev.map((c) => c.companyId === companyId
                ? Object.assign(Object.assign({}, c), { forecastDataMonth: value, lastUpdated: new Date().toLocaleDateString("en-US") }) : c));
            enqueueSnackbar("Forecast maand succesvol bijgewerkt", {
                variant: "success",
            });
        }
        catch (_a) {
            enqueueSnackbar("Fout bij het bijwerken van forecast maand", {
                variant: "error",
            });
        }
    });
    const handleAddCompanyConfig = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedCompany || !selectedMonth) {
            enqueueSnackbar("Selecteer een bedrijf en maand", {
                variant: "warning",
            });
            return;
        }
        try {
            if (!canCreate("CompanyConfigurations")) {
                enqueueSnackbar("U heeft geen toestemming om configuraties aan te maken", {
                    variant: "error",
                });
                return;
            }
            yield createConfig({
                companyId: selectedCompany.entiteitId,
                configMonth: selectedMonth,
                pocReadOnly: false,
            }).unwrap();
            // Refetch configs to update the list
            refetchConfigs();
            setAddDialogOpen(false);
            setSelectedCompany(null);
            setSelectedMonth(getCurrentMonth());
            enqueueSnackbar("Bedrijfsconfiguratie succesvol toegevoegd", {
                variant: "success",
            });
        }
        catch (_a) {
            enqueueSnackbar("Fout bij het toevoegen van configuratie", {
                variant: "error",
            });
        }
    });
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const handleThresholdChange = (companyId, value) => {
        setCompanies((prev) => prev.map((c) => c.companyId === companyId ? Object.assign(Object.assign({}, c), { thresholdAmount: value }) : c));
    };
    const handleMarginChange = (companyId, value) => {
        const parsed = value === "" ? "" : Math.max(0, Math.min(100, Number(value)));
        setCompanies((prev) => prev.map((c) => c.companyId === companyId ? Object.assign(Object.assign({}, c), { marginPercentage: parsed }) : c));
    };
    // Open the bulk close confirmation dialog (no server call on blur for threshold/margin)
    const handleRowBulkProcess = (company) => __awaiter(void 0, void 0, void 0, function* () {
        if (!company.forecastDataMonth) {
            enqueueSnackbar("Selecteer eerst een maand voor dit bedrijf", {
                variant: "warning",
            });
            return;
        }
        if (!canUpdate("CompanyConfigurations")) {
            enqueueSnackbar("U heeft geen toestemming voor deze actie", {
                variant: "error",
            });
            return;
        }
        // Set company for dialog and open it
        setBulkCloseCompany(company);
        setBulkCloseDialogOpen(true);
    });
    // Confirm bulk close: save threshold/margin first, then trigger bulk process
    const handleConfirmBulkClose = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!bulkCloseCompany)
            return;
        setBulkCloseLoading(true);
        const company = bulkCloseCompany;
        const thresholdAmount = company.thresholdAmount === undefined || company.thresholdAmount === ""
            ? 0
            : Number(company.thresholdAmount);
        const marginPercentage = company.marginPercentage === undefined || company.marginPercentage === ""
            ? 0
            : Number(company.marginPercentage);
        try {
            // First, save threshold and margin to server if the config exists
            if (company.id && (thresholdAmount > 0 || marginPercentage > 0)) {
                yield updateConfig({
                    id: company.id,
                    companyId: company.companyId,
                    configMonth: company.forecastDataMonth,
                    pocReadOnly: company.pocAanpassen,
                    thresholdAmount,
                    marginPercentage,
                }).unwrap();
            }
            // Close dialog immediately
            setBulkCloseLoading(false);
            setBulkCloseDialogOpen(false);
            setBulkCloseCompany(null);
            // Trigger bulk process
            const result = yield bulkProcess({
                companyId: company.companyId,
                month: company.forecastDataMonth,
                thresholdAmount,
                marginPercentage,
            }).unwrap();
            enqueueSnackbar(result.message || "Bulk verwerking gestart voor " + company.name, { variant: "success" });
            // Join the SignalR group for this company to receive real-time updates
            if (result.jobId) {
                // Set initial "Queued" status in activeJobs
                setActiveJobs((prev) => (Object.assign(Object.assign({}, prev), { [company.companyId]: {
                        jobId: result.jobId,
                        companyId: company.companyId,
                        status: "Queued",
                        totalProjects: 0,
                        processedSoFar: 0,
                        updated: 0,
                        skipped: 0,
                        progressPercent: 0,
                        errorMessage: null,
                    } })));
                // Remember the company name for notifications
                companyNameMapRef.current[company.companyId] = company.name;
                // Join SignalR group
                const conn = connectionRef.current;
                if (conn && conn.state === signalR.HubConnectionState.Connected) {
                    try {
                        yield conn.invoke("JoinCompanyGroup", company.companyId);
                        joinedGroupsRef.current.add(company.companyId);
                    }
                    catch (_a) {
                        // silently ignore
                    }
                }
            }
        }
        catch (_b) {
            enqueueSnackbar("Fout bij bulk verwerking", { variant: "error" });
        }
    });
    // Filter companies based on search
    const filteredCompanies = companies.filter((company) => company.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()));
    // Paginate companies
    const paginatedCompanies = filteredCompanies.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    // Get company initials for avatar
    const getCompanyInitials = (name) => {
        const words = name.split(" ");
        if (words.length >= 2) {
            return (words[0][0] + words[1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };
    return (React.createElement(Box, { sx: {
            display: "flex",
            flexDirection: "column",
            gap: 3,
        } },
        React.createElement(Box, null,
            React.createElement(Tabs, { value: tabValue, onChange: handleTabChange, "aria-label": "settings tabs", variant: "scrollable" },
                React.createElement(Tab, { label: "Gebruikersbeheer" }),
                React.createElement(Tab, { label: "Rollen & Machtigingen" }),
                React.createElement(Tab, { label: "Bedrijfsconfiguraties" }))),
        React.createElement(TabPanel, { value: tabValue, index: 0 },
            React.createElement(Users, null)),
        React.createElement(TabPanel, { value: tabValue, index: 1 },
            React.createElement(RolePermissions, { isInsideSettings: true })),
        React.createElement(TabPanel, { value: tabValue, index: 2 }, permissionsLoading ? (React.createElement(Box, { sx: { height: 300 } },
            React.createElement(WidgetLoader, { isLoading: true }))) : !canRead("CompanyConfigurations") ? (React.createElement(NoModuleAccess, { message: "U heeft geen toegang tot de bedrijfsconfiguraties module" })) : configsFailed ? (React.createElement(Typography, { color: "error", sx: { p: 3 } }, "Fout bij het ophalen van de bedrijfsconfiguraties. Probeer het later opnieuw.")) : companiesLoading || configsLoading ? (React.createElement(Box, { sx: {
                p: 3,
                backgroundColor: "background.paper",
                borderRadius: 2,
            } },
            React.createElement(Box, { sx: { mb: 3 } },
                React.createElement(Skeleton, { variant: "rounded", width: "100%", height: 48 })),
            React.createElement(TableContainer, null,
                React.createElement(Table, null,
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, { sx: {
                                    backgroundColor: theme.palette.grey[50],
                                    minWidth: isMobile ? 150 : 200,
                                } },
                                React.createElement(Skeleton, { variant: "text", width: "70%", height: 24 })),
                            !isMobile && (React.createElement(TableCell, { align: "center", sx: {
                                    backgroundColor: theme.palette.grey[50],
                                    minWidth: 120,
                                } },
                                React.createElement(Skeleton, { variant: "text", width: "70%", height: 24 }))),
                            !isMobile && (React.createElement(TableCell, { align: "center", sx: {
                                    backgroundColor: theme.palette.grey[50],
                                    minWidth: 120,
                                } },
                                React.createElement(Skeleton, { variant: "text", width: "70%", height: 24 }))),
                            React.createElement(TableCell, { align: "center", sx: { backgroundColor: theme.palette.grey[50] } },
                                React.createElement(Skeleton, { variant: "text", width: "70%", height: 24 })))),
                    React.createElement(TableBody, null, Array.from({ length: 8 }).map((_, index) => (React.createElement(TableRow, { key: index },
                        React.createElement(TableCell, null,
                            React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 2 } },
                                React.createElement(Skeleton, { variant: "circular", width: 32, height: 32 }),
                                React.createElement(Skeleton, { variant: "text", width: "60%", height: 20 }))),
                        !isMobile && (React.createElement(TableCell, { align: "center" },
                            React.createElement(Skeleton, { variant: "rectangular", width: 24, height: 24, sx: { mx: "auto" } }))),
                        !isMobile && (React.createElement(TableCell, { align: "center" },
                            React.createElement(Skeleton, { variant: "text", width: "60%", height: 20, sx: { mx: "auto" } }))),
                        React.createElement(TableCell, { align: "center" },
                            React.createElement(Skeleton, { variant: "rounded", width: "70%", height: 32, sx: { mx: "auto", borderRadius: "8px" } })))))))),
            React.createElement(Box, { sx: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pt: 2,
                    mt: 2,
                } },
                React.createElement(Skeleton, { variant: "text", width: "15%", height: 24 }),
                React.createElement(Box, { sx: { display: "flex", gap: 1 } },
                    React.createElement(Skeleton, { variant: "circular", width: 32, height: 32 }),
                    React.createElement(Skeleton, { variant: "circular", width: 32, height: 32 }),
                    React.createElement(Skeleton, { variant: "circular", width: 32, height: 32 }))))) : (React.createElement(React.Fragment, null,
            React.createElement(PageHeader, { startActions: React.createElement(FilteredInput, { placeholder: "Zoeken", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), endIcon: React.createElement(SearchNormal, { size: 20, color: "currentColor" }) }) }),
            isTablet ? (React.createElement(Box, { sx: {
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: "hidden",
                } },
                paginatedCompanies.map((company) => (React.createElement(CompanyConfigCard, { key: company.companyId, company: company, canUpdate: canUpdate("CompanyConfigurations"), activeJob: activeJobs[company.companyId], openPickerId: openPickerId, setOpenPickerId: setOpenPickerId, onPocToggle: handlePocToggle, onForecastMonthChange: handleForecastMonthChange, onThresholdChange: handleThresholdChange, onMarginChange: handleMarginChange, onBulkProcess: handleRowBulkProcess }))),
                React.createElement(CardPagination, { count: filteredCompanies.length, page: page, pageSize: rowsPerPage, onPageChange: (newPage) => setPage(newPage), onPageSizeChange: (s) => {
                        setRowsPerPage(s);
                        setPage(0);
                    }, pageSizeOptions: [10, 20, 30, 50, 100] }))) : (React.createElement(Box, { sx: {
                    backgroundColor: "background.paper",
                    borderRadius: "24px",
                    boxShadow: "0px 12px 24px -4px #919EAB1F,0px 0px 2px 0px #919EAB33",
                    overflow: "hidden",
                    height: isMobile
                        ? "calc(100dvh - 280px)"
                        : isTablet
                            ? "calc(100dvh - 320px)"
                            : "calc(100dvh - 290px)",
                    display: "flex",
                    flexDirection: "column",
                } },
                React.createElement(TableContainer, { sx: {
                        flexGrow: 1,
                        overflow: "auto",
                        borderRadius: "0px",
                        boxShadow: "none",
                    } },
                    React.createElement(Table, { stickyHeader: true },
                        React.createElement(TableHead, null,
                            React.createElement(TableRow, null,
                                React.createElement(TableCell, { sx: {
                                        fontWeight: 500,
                                        color: theme.palette.tertiary[30],
                                        backgroundColor: theme.palette.grey[50],
                                        minWidth: isMobile ? 150 : 200,
                                    } }, "Bedrijfsnaam"),
                                !isMobile && (React.createElement(TableCell, { align: "center", sx: {
                                        fontWeight: 500,
                                        color: theme.palette.tertiary[30],
                                        backgroundColor: theme.palette.grey[50],
                                        minWidth: 120,
                                    } }, "POC Aanpassen")),
                                !isMobile && (React.createElement(TableCell, { align: "center", sx: {
                                        fontWeight: 500,
                                        color: theme.palette.tertiary[30],
                                        backgroundColor: theme.palette.grey[50],
                                        minWidth: 120,
                                    } }, "Laatst aangepast")),
                                React.createElement(TableCell, { align: "center", sx: {
                                        fontWeight: 500,
                                        color: theme.palette.tertiary[30],
                                        backgroundColor: theme.palette.grey[50],
                                    } }, "Huidige maand"),
                                React.createElement(TableCell, { align: "center", sx: {
                                        fontWeight: 500,
                                        color: theme.palette.tertiary[30],
                                        backgroundColor: theme.palette.grey[50],
                                        minWidth: 140,
                                    } }, "Maximale aanneemsom"),
                                React.createElement(TableCell, { align: "center", sx: {
                                        fontWeight: 500,
                                        color: theme.palette.tertiary[30],
                                        backgroundColor: theme.palette.grey[50],
                                        minWidth: 120,
                                    } }, "Marge %"),
                                React.createElement(TableCell, { align: "center", sx: {
                                        fontWeight: 500,
                                        color: theme.palette.tertiary[30],
                                        backgroundColor: theme.palette.grey[50],
                                        minWidth: 160,
                                    } }))),
                        React.createElement(TableBody, null, paginatedCompanies.map((company) => {
                            var _a, _b, _c, _d, _e;
                            return (React.createElement(TableRow, { key: company.companyId, hover: true, sx: {
                                    "&:last-child td, &:last-child th": { border: 0 },
                                } },
                                React.createElement(TableCell, null,
                                    React.createElement(Box, { sx: {
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        } },
                                        React.createElement(Avatar, { sx: {
                                                width: 32,
                                                height: 32,
                                                fontSize: "14px",
                                                bgcolor: theme.palette.grey[300],
                                                color: theme.palette.grey[600],
                                            } }, getCompanyInitials(company.name)),
                                        React.createElement(Box, null,
                                            React.createElement(Typography, { variant: "body2" }, company.name),
                                            isMobile && (React.createElement(Box, { sx: { mt: 0.5 } },
                                                React.createElement(Typography, { variant: "caption", sx: {
                                                        color: "text.secondary",
                                                    } }, company.lastUpdated)))))),
                                !isMobile && (React.createElement(TableCell, { align: "center" },
                                    React.createElement(Tooltip, { title: "POC aanpassen aan-/uitzetten", arrow: true },
                                        React.createElement("span", null,
                                            React.createElement(Checkbox, { checked: company.pocAanpassen, onChange: () => handlePocToggle(company.companyId), disabled: !canUpdate("CompanyConfigurations"), icon: React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                                                    React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5 0C2.23858 0 0 2.23858 0 5V15C0 17.7614 2.23858 20 5 20H15C17.7614 20 20 17.7614 20 15V5C20 2.23858 17.7614 0 15 0H5ZM5.5 1.5C3.29086 1.5 1.5 3.29086 1.5 5.5V14.5C1.5 16.7091 3.29086 18.5 5.5 18.5H14.5C16.7091 18.5 18.5 16.7091 18.5 14.5V5.5C18.5 3.29086 16.7091 1.5 14.5 1.5H5.5Z", fill: theme.palette.primary.main })), checkedIcon: React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                                                    React.createElement("path", { d: "M15 0C17.7614 0 20 2.23858 20 5V15C20 17.7614 17.7614 20 15 20H5C2.23858 20 0 17.7614 0 15V5C0 2.23858 2.23858 0 5 0H15ZM14.6152 7.25488C14.2753 6.9149 13.715 6.91494 13.375 7.25488L9.24512 11.3848L7.49512 9.63477C7.15513 9.29484 6.59487 9.29484 6.25488 9.63477C5.9149 9.97475 5.91494 10.525 6.25488 10.875L8.63477 13.2451C8.80477 13.4151 9.02512 13.4951 9.24512 13.4951C9.47507 13.4951 9.69526 13.4151 9.86523 13.2451L14.6152 8.49512C14.9552 8.15513 14.9552 7.60487 14.6152 7.25488Z", fill: theme.palette.primary.main })), sx: {
                                                    "&.Mui-disabled svg path": {
                                                        fill: theme.palette.primary.light,
                                                    },
                                                } }))))),
                                !isMobile && (React.createElement(TableCell, { align: "center" },
                                    React.createElement(Typography, { variant: "body2" }, company.lastUpdated))),
                                React.createElement(TableCell, { align: "center" },
                                    React.createElement(LocalizationProvider, { dateAdapter: AdapterDayjs },
                                        React.createElement(DatePicker, { views: ["year", "month"], openTo: "year", open: openPickerId === company.companyId, onOpen: () => setOpenPickerId(company.companyId), onClose: () => setOpenPickerId(null), value: company.forecastDataMonth
                                                ? dayjs(company.forecastDataMonth, "MMM, YYYY")
                                                : null, onChange: (date) => {
                                                if (date) {
                                                    handleForecastMonthChange(company.companyId, date.format("MMM, YYYY"));
                                                }
                                            }, maxDate: dayjs(), disabled: !canUpdate("CompanyConfigurations"), slotProps: {
                                                textField: {
                                                    size: "small",
                                                    onClick: () => {
                                                        if (canUpdate("CompanyConfigurations")) {
                                                            setOpenPickerId(company.companyId);
                                                        }
                                                    },
                                                    sx: {
                                                        minWidth: 150,
                                                        "& .MuiOutlinedInput-root, & .MuiPickersOutlinedInput-root": {
                                                            borderRadius: "40px",
                                                            cursor: "pointer",
                                                            "& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline": {
                                                                borderColor: `rgba(145, 158, 171, 0.2)`,
                                                            },
                                                            "&:hover .MuiOutlinedInput-notchedOutline, &:hover .MuiPickersOutlinedInput-notchedOutline": {
                                                                borderColor: `rgba(145, 158, 171, 0.2)`,
                                                            },
                                                        },
                                                        "& .MuiInputBase-input, & .MuiPickersSectionList-root": {
                                                            cursor: "pointer",
                                                        },
                                                    },
                                                },
                                            } }))),
                                React.createElement(TableCell, { align: "center" },
                                    React.createElement(TextField, { type: "number", size: "small", value: (_a = company.thresholdAmount) !== null && _a !== void 0 ? _a : "", onChange: (e) => handleThresholdChange(company.companyId, e.target.value), sx: {
                                            width: isMobile ? 120 : 140,
                                            borderRadius: "40px",
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "40px",
                                            },
                                            "& .MuiInputBase-input": {
                                                borderRadius: "40px",
                                            },
                                        }, slotProps: {
                                            input: { inputProps: { min: 0 } },
                                        } })),
                                React.createElement(TableCell, { align: "center" },
                                    React.createElement(TextField, { type: "number", size: "small", value: (_b = company.marginPercentage) !== null && _b !== void 0 ? _b : "", onChange: (e) => handleMarginChange(company.companyId, e.target.value), sx: {
                                            width: isMobile ? 100 : 120,
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "40px",
                                            },
                                        }, slotProps: {
                                            input: {
                                                inputProps: { min: 0, max: 100 },
                                                endAdornment: (React.createElement(Typography, { variant: "body2", sx: {
                                                        color: "text.secondary",
                                                        ml: 0.5,
                                                    } }, "%")),
                                            },
                                        } })),
                                React.createElement(TableCell, { align: "center" }, activeJobs[company.companyId] ? (React.createElement(Box, { sx: {
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "stretch",
                                        gap: 0.5,
                                        width: 140,
                                        mx: "auto",
                                    } },
                                    React.createElement(Box, { sx: {
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        } },
                                        React.createElement(Typography, { variant: "caption", sx: {
                                                fontWeight: 600,
                                                color: theme.palette.primary.dark,
                                            } }, activeJobs[company.companyId].status ===
                                            "Queued"
                                            ? "In Wachtrij..."
                                            : activeJobs[company.companyId].status ===
                                                "Processing"
                                                ? activeJobs[company.companyId]
                                                    .processedSoFar != null &&
                                                    activeJobs[company.companyId]
                                                        .totalProjects != null
                                                    ? `${activeJobs[company.companyId].processedSoFar}/${activeJobs[company.companyId].totalProjects}`
                                                    : "Verwerken..."
                                                : activeJobs[company.companyId]
                                                    .status === "Completed"
                                                    ? "Voltooid"
                                                    : "Mislukt"),
                                        React.createElement(Typography, { variant: "caption", sx: {
                                                fontWeight: 500,
                                                color: theme.palette.text.secondary,
                                            } }, (_c = activeJobs[company.companyId]
                                            .progressPercent) !== null && _c !== void 0 ? _c : 0,
                                            "%")),
                                    React.createElement(Box, { sx: {
                                            width: "100%",
                                            height: 3,
                                            borderRadius: 1.5,
                                            backgroundColor: theme.palette.grey[200],
                                            overflow: "hidden",
                                        } },
                                        React.createElement(Box, { sx: {
                                                width: activeJobs[company.companyId]
                                                    .totalProjects > 0
                                                    ? `${activeJobs[company.companyId].progressPercent}%`
                                                    : "0%",
                                                height: "100%",
                                                borderRadius: 2,
                                                backgroundColor: theme.palette.primary.main,
                                                transition: "width 0.3s ease",
                                            } })))) : (React.createElement(Button, { variant: "outlined", size: "large", onClick: () => handleRowBulkProcess(company), disabled: !canUpdate("CompanyConfigurations") ||
                                        ((!company.thresholdAmount ||
                                            Number(company.thresholdAmount) === 0) &&
                                            (!company.marginPercentage ||
                                                Number(company.marginPercentage) === 0)), sx: {
                                        textTransform: "none",
                                        fontWeight: 500,
                                        fontSize: "0.8125rem",
                                        whiteSpace: "nowrap",
                                        borderRadius: "20px",
                                        border: "none",
                                        backgroundColor: (_d = theme.palette.primary[10]) !== null && _d !== void 0 ? _d : "#EFF9FF",
                                        color: theme.palette.primary.dark,
                                        width: 140,
                                        py: 0.75,
                                        boxShadow: "none",
                                        "&:hover": {
                                            backgroundColor: (_e = theme.palette.primary[20]) !== null && _e !== void 0 ? _e : "#BBD8EB",
                                            border: "none",
                                            boxShadow: "none",
                                        },
                                        "&.Mui-disabled": {
                                            backgroundColor: "#919EAB33",
                                            color: "#919EABCC",
                                            border: "none",
                                        },
                                    } }, "Massa forecasten")))));
                        })))),
                React.createElement(TablePagination, { rowsPerPageOptions: [10, 20, 30, 50, 100], component: "div", count: filteredCompanies.length, rowsPerPage: rowsPerPage, page: page, onPageChange: handleChangePage, onRowsPerPageChange: handleChangeRowsPerPage, labelRowsPerPage: "Rijen per pagina:", sx: {
                        flexShrink: 0,
                        "& .MuiTablePagination-displayedRows": {
                            margin: 0,
                        },
                        color: theme.palette.tertiary[30],
                        backgroundColor: "#F4F6F8",
                        borderRadius: "8px 8px 24px 24px",
                    } })))))),
        React.createElement(Dialog, { open: bulkCloseDialogOpen, onClose: () => {
                if (!bulkCloseLoading) {
                    setBulkCloseDialogOpen(false);
                    setBulkCloseCompany(null);
                }
            }, maxWidth: "xs", fullWidth: true, slotProps: {
                paper: {
                    sx: {
                        borderRadius: "16px",
                        p: 0,
                        maxWidth: "480px !important",
                    },
                },
            } },
            React.createElement(DialogTitle, { sx: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    px: 3.5,
                    pt: 3.5,
                    pb: 1.5,
                } },
                React.createElement(Typography, { variant: "h6", sx: {
                        fontWeight: 600,
                    } }, "Bulk afronden"),
                React.createElement(Tooltip, { title: "Sluiten", arrow: true },
                    React.createElement(IconButton, { onClick: () => {
                            if (!bulkCloseLoading) {
                                setBulkCloseDialogOpen(false);
                                setBulkCloseCompany(null);
                            }
                        }, size: "small", sx: { mt: -0.5 } },
                        React.createElement(CloseCircle, { size: 20 })))),
            React.createElement(DialogContent, { sx: { px: 3.5, pt: 0.5, pb: 0 } }, bulkCloseCompany && (React.createElement(Typography, { variant: "body1", sx: {
                    color: "text.secondary",
                    lineHeight: 1.45,
                    maxWidth: 500,
                } },
                "Weet u zeker dat u alle projecten met een drempelbedrag van",
                " ",
                React.createElement(Typography, { component: "span", variant: "body1", sx: {
                        fontWeight: 600,
                        color: "primary.main",
                    } }, bulkCloseCompany.thresholdAmount || 0),
                " ",
                "wilt forecasten met een marge van",
                " ",
                React.createElement(Typography, { component: "span", variant: "body1", sx: {
                        fontWeight: 600,
                        color: "primary.main",
                    } },
                    bulkCloseCompany.marginPercentage || 0,
                    "%"),
                "?"))),
            React.createElement(DialogActions, { sx: {
                    px: 3.5,
                    pt: 3,
                    pb: 3.5,
                    justifyContent: "flex-end",
                } },
                React.createElement(Button, { onClick: () => {
                        setBulkCloseDialogOpen(false);
                        setBulkCloseCompany(null);
                    }, disabled: bulkCloseLoading, variant: "outlined", sx: {
                        borderRadius: 5,
                        textTransform: "none",
                        color: "text.primary",
                        borderColor: theme.palette.grey[300],
                        minWidth: 88,
                        height: 42,
                    } }, "Cancel"),
                React.createElement(Button, { onClick: handleConfirmBulkClose, disabled: bulkCloseLoading, variant: "contained", sx: {
                        borderRadius: 5,
                        textTransform: "none",
                        backgroundColor: theme.palette.primary.main,
                        minWidth: 190,
                        height: 42,
                    } }, bulkCloseLoading ? "Verwerken..." : "Bevestigen & Sluiten"))),
        React.createElement(Dialog, { open: addDialogOpen, onClose: () => setAddDialogOpen(false), maxWidth: "sm", fullWidth: true },
            React.createElement(DialogTitle, null, "Bedrijfsconfiguratie toevoegen"),
            React.createElement(DialogContent, null,
                React.createElement(Box, { sx: { pt: 2, display: "flex", flexDirection: "column", gap: 3 } },
                    React.createElement(Autocomplete, { value: selectedCompany, onChange: (event, newValue) => setSelectedCompany(newValue), options: (companiesData === null || companiesData === void 0 ? void 0 : companiesData.filter((comp) => !companies.find((c) => c.companyId === comp.entiteitId))) || [], getOptionLabel: (option) => option.entiteitNaam, renderInput: (params) => (React.createElement(TextField, Object.assign({}, params, { label: "Bedrijf", placeholder: "Selecteer een bedrijf", required: true }))) }),
                    React.createElement(LocalizationProvider, { dateAdapter: AdapterDayjs },
                        React.createElement(DatePicker, { views: ["month", "year"], openTo: "month", label: "Selecteer maand en jaar", value: selectedMonth ? dayjs(selectedMonth, "MMM, YYYY") : null, onChange: (date) => {
                                if (date)
                                    setSelectedMonth(date.format("MMM, YYYY"));
                            }, slotProps: {
                                textField: {
                                    fullWidth: true,
                                },
                            } })))),
            React.createElement(DialogActions, null, isMobile ? (React.createElement(React.Fragment, null,
                React.createElement(Tooltip, { title: "Sluiten", arrow: true },
                    React.createElement(IconButton, { onClick: () => setAddDialogOpen(false), sx: {
                            color: "text.secondary",
                        } },
                        React.createElement(CloseCircle, { size: 20 }))),
                React.createElement(Tooltip, { title: "Bedrijfsconfiguratie toevoegen", arrow: true },
                    React.createElement("span", null,
                        React.createElement(IconButton, { onClick: handleAddCompanyConfig, disabled: !selectedCompany, sx: {
                                backgroundColor: theme.palette.primary.main,
                                color: "white",
                                "&:hover": {
                                    backgroundColor: theme.palette.primary.dark,
                                },
                                "&.Mui-disabled": {
                                    backgroundColor: theme.palette.action.disabledBackground,
                                    color: theme.palette.action.disabled,
                                },
                            } },
                            React.createElement(Add, { size: 20 })))))) : (React.createElement(React.Fragment, null,
                React.createElement(Button, { onClick: () => setAddDialogOpen(false) }, "Annuleren"),
                React.createElement(Button, { onClick: handleAddCompanyConfig, variant: "contained", disabled: !selectedCompany }, "Toevoegen")))))));
};
export default Settings;
